/** Marketing i18n fragment — intgFreshdesk (integrations/freshdesk). */

export const intgFreshdeskEn = {
  meta: {
    title: "Freshdesk integration — ticket sync, internal notes and approved replies",
    description:
      "Mirror tickets between Freshdesk and FixControl, write internal notes, harvest solved tickets and the knowledge base, and send customer replies that a person approved first. Syncs are off by default, health is tracked per capability, and writes go through a retried outbox.",
  },
  hero: {
    eyebrow: "Freshdesk integration",
    title: "Tickets synced two ways,",
    highlight: "replies held for approval",
    subtitle:
      "Tickets mirror between Freshdesk and FixControl so support and engineering work the same record. A reply to a customer of your organization is drafted by AI and held until someone on your team approves it — then it is sent through a monitored delivery queue and logged.",
    primaryCta: "Book a demo",
    secondaryCta: "See approved replies",
    bullet1: "Drafts held for approval",
    bullet2: "Syncs off by default",
    bullet3: "Per-capability health",
  },
  what: {
    eyebrow: "What it does",
    title: "What the Freshdesk connection covers",
  },
  features: {
    twoWaySync: {
      title: "Two-way ticket sync",
      body: "Tickets mirror between Freshdesk and FixControl within your tenant. Support context and engineering remediation stay attached to the same record on both sides.",
    },
    approvedReplies: {
      title: "Approved customer replies",
      body: "AI drafts a public reply; the draft is held until a person approves it, and only an approved draft is sent. The approval is recorded before the send is dispatched.",
    },
    internalNotes: {
      title: "Internal notes",
      body: "Notes for agents and engineers go on the ticket without touching the customer-facing thread. Working context gets captured where the work is.",
    },
    knowledgeHarvest: {
      title: "Knowledge harvest",
      body: "Solved tickets and knowledge-base articles are harvested into support context, where the connection has access to read them. Answers draw on your own material.",
    },
    supportContext: {
      title: "Support context",
      body: "The ticket, its history, and related knowledge are pulled into one governed work item. A reply or a fix is written against the case as it stands.",
    },
    evidenceAudit: {
      title: "Evidence & audit",
      body: "Every write to Freshdesk is recorded with who approved it and what evidence they saw. The ticket history and the decision ledger stay consistent.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Which Freshdesk paths are working, shown per path",
    description:
      "Ticket read, public reply, note write, harvest, and sync are separate capabilities with separate requirements. FixControl probes each one and shows its state, instead of a single connected badge that can hide a broken reply path.",
    readTickets: {
      name: "Read tickets & history",
      body: "Reads tickets and their history within the bound project and tenant, to build support context and answer questions.",
    },
    publicReply: {
      name: "Customer public reply",
      body: "Needs reply-write scope on the connection and a recorded human approval per reply. Drafts don’t leave FixControl without one.",
    },
    internalNote: {
      name: "Internal note",
      body: "Written through the outbox as attributed actions. Visible to agents and engineers, not to the customer.",
    },
    kbHarvest: {
      name: "Knowledge-base harvest",
      body: "Works where the connection can read the knowledge base and solved tickets; access scope and available content set the limits.",
    },
    twoWaySync: {
      name: "Two-way ticket sync",
      body: "Each sync is off by default and enabled per connection, under the configured credentials. Enabled paths write through the outbox with retry.",
    },
    singleStatus: {
      name: "A single ‘connected’ badge",
      body: "Not part of the model. Ticket read can work while reply or harvest doesn’t, and the health view says so.",
    },
  },
  governed: {
    eyebrow: "When a send fails",
    title: "A reply is either sent or visibly failed",
    noteTitle: "Approval first, outbox second",
    noteBody:
      "A customer reply exists as a draft until a person approves it; only then is the send dispatched. Every outbound Freshdesk write — reply, note, or sync update — goes through the integration outbox, retrying transient failures and dead-lettering persistent ones onto the operational timeline. The approving decision is stored in the decision ledger with actor, role, channel, evidence, and consequence.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions about the Freshdesk integration",
    q1: {
      q: "Does FixControl reply to customers automatically?",
      a: "No. A reply starts as an AI draft and is held until a person approves it. The send happens after the approval is recorded — not before.",
    },
    q2: {
      q: "What’s the difference between a reply and an internal note?",
      a: "A public reply reaches the customer and requires approval plus reply-write scope. An internal note stays with agents and engineers. FixControl keeps the two as separate write paths, so a note can’t go out as a reply.",
    },
    q3: {
      q: "What happens if a Freshdesk write fails?",
      a: "The write stays queued in the outbox and retries. If Freshdesk keeps refusing it, the job dead-letters and the timeline shows a failed send — the ticket won’t pretend the reply went out.",
    },
    q4: {
      q: "Are two-way syncs on by default?",
      a: "No. Syncs are off by default and enabled per connection. Capability health then shows which enabled paths are configured and working.",
    },
    q5: {
      q: "Are Freshdesk replies audited?",
      a: "Yes. Every approved reply and note has a decision-ledger entry recording who approved it and on what evidence, matching what the ticket history shows.",
    },
  },
  related: {
    approvedReplies: {
      label: "Approved replies",
      description: "The approval flow behind every customer-facing reply.",
    },
    supportToEng: {
      label: "Support → engineering",
      description: "Escalate a ticket into a governed engineering fix.",
    },
    jira: {
      label: "Jira",
      description: "Freshdesk context feeding governed Jira actions.",
    },
  },
  cta: {
    title: "Watch a reply wait for approval",
    description:
      "Book a demo: a ticket syncs in, AI drafts the reply, and the send waits until someone approves it.",
    primary: "Book a demo",
    secondary: "Ask FixControl",
  },
};

export const intgFreshdeskNl = {
  meta: {
    title: "Freshdesk-integratie — ticket-sync, interne notities en goedgekeurde antwoorden",
    description:
      "Spiegel tickets tussen Freshdesk en FixControl, schrijf interne notities, oogst opgeloste tickets en de kennisbank, en verstuur klantantwoorden die een mens eerst goedkeurde. Syncs staan standaard uit, health wordt per capability bijgehouden en schrijfacties lopen via een herprobeerde outbox.",
  },
  hero: {
    eyebrow: "Freshdesk-integratie",
    title: "Tweerichtings-sync en",
    highlight: "goedgekeurde antwoorden",
    subtitle:
      "Tickets spiegelen tussen Freshdesk en FixControl, zodat support en engineering aan hetzelfde record werken. Een antwoord aan een klant van je organisatie wordt door AI opgesteld en vastgehouden tot iemand van je team het goedkeurt — dan wordt het via een bewaakte leveringswachtrij verzonden en gelogd.",
    primaryCta: "Boek een demo",
    secondaryCta: "Bekijk goedgekeurde antwoorden",
    bullet1: "Concepten wachten op goedkeuring",
    bullet2: "Syncs standaard uit",
    bullet3: "Health per capability",
  },
  what: {
    eyebrow: "Wat het doet",
    title: "Wat de Freshdesk-koppeling dekt",
  },
  features: {
    twoWaySync: {
      title: "Tweerichtings ticket-sync",
      body: "Tickets spiegelen tussen Freshdesk en FixControl, zodat support-context en engineering-remediatie op hetzelfde record blijven — gescoped tot je tenant.",
    },
    approvedReplies: {
      title: "Goedgekeurde klantantwoorden",
      body: "AI stelt een openbaar antwoord op; het concept wordt vastgehouden tot een mens het goedkeurt, en alleen een goedgekeurd concept wordt verzonden. De goedkeuring wordt vastgelegd voordat de verzending in gang gaat.",
    },
    internalNotes: {
      title: "Interne notities",
      body: "Schrijf interne notities voor agents en engineers op een ticket, zodat de werkcontext wordt vastgelegd zonder de klantgerichte thread te raken.",
    },
    knowledgeHarvest: {
      title: "Kennis-oogst",
      body: "Oogst opgeloste tickets en de kennisbank in support-context, waar toegang en content dat toelaten, zodat antwoorden geworteld blijven in je eigen materiaal.",
    },
    supportContext: {
      title: "Support-context",
      body: "Trek het ticket, zijn historie en gerelateerde kennis in een gegoverneerd werkitem, zodat een antwoord of remediatie geworteld is in de werkelijke case.",
    },
    evidenceAudit: {
      title: "Bewijs & audit",
      body: "Elke gegoverneerde write wordt vastgelegd met actor, rol en het getoonde bewijs, zodat de tickethistorie en het beslissingsregister overeenkomen.",
    },
  },
  capabilities: {
    eyebrow: "Capabilities & health",
    title: "Welke Freshdesk-paden werken, per pad getoond",
    description:
      "Tickets lezen, openbaar antwoorden, notities schrijven, oogsten en synchroniseren zijn aparte capabilities met eigen vereisten. FixControl test elk ervan en toont zijn staat, in plaats van één ‘connected’-badge die een kapot antwoordpad kan verbergen.",
    readTickets: {
      name: "Tickets & historie lezen",
      body: "FixControl leest tickets en hun historie binnen het gekoppelde project en de tenant om support-context samen te stellen en vragen te beantwoorden.",
    },
    publicReply: {
      name: "Openbaar klantantwoord",
      body: "Vereist reply-write-scope op de verbinding en een vastgelegde menselijke goedkeuring per antwoord. Zonder die goedkeuring verlaat een concept FixControl niet.",
    },
    internalNote: {
      name: "Interne notitie",
      body: "Interne notities worden geschreven als een vastgelegde, toewijsbare actie via de outbox, zichtbaar voor agents en engineers, niet voor de klant.",
    },
    kbHarvest: {
      name: "Kennisbank-oogst",
      body: "Ondersteund waar de verbinding de kennisbank en opgeloste tickets kan lezen; afhankelijk van de toegangsscope en de content die te oogsten valt.",
    },
    twoWaySync: {
      name: "Tweerichtings ticket-sync",
      body: "Elke sync staat standaard uit en wordt per verbinding ingeschakeld, onder de geconfigureerde credentials. Ingeschakelde paden schrijven via de outbox met retry.",
    },
    singleStatus: {
      name: "Eén ‘connected’-badge",
      body: "Geen onderdeel van het model. Tickets lezen kan werken terwijl antwoorden of oogsten dat niet doet, en de health-weergave zegt dat.",
    },
  },
  governed: {
    eyebrow: "Als een verzending faalt",
    title: "Een antwoord wordt verzonden of faalt zichtbaar",
    noteTitle: "Eerst goedkeuring, dan de outbox",
    noteBody:
      "Een klantantwoord bestaat als concept totdat een mens het goedkeurt; pas dan wordt de verzending in gang gezet. Elke uitgaande Freshdesk-schrijfactie — een antwoord, een notitie of een sync-update — loopt via de integratie-outbox, die tijdelijke fouten opnieuw probeert en hardnekkige naar de dead-letter op de operationele tijdlijn zet. De goedkeurende beslissing wordt in het beslissingsregister bewaard met actor, rol, kanaal, bewijs en consequentie.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen over de Freshdesk-integratie",
    q1: {
      q: "Antwoordt FixControl automatisch aan klanten?",
      a: "Nee. Een antwoord begint als AI-concept en wordt vastgehouden tot een mens het goedkeurt. De verzending gebeurt nadat de goedkeuring is vastgelegd — niet ervoor.",
    },
    q2: {
      q: "Wat is het verschil tussen een antwoord en een interne notitie?",
      a: "Een openbaar antwoord bereikt de klant en vereist goedkeuring plus reply-write-scope. Een interne notitie blijft bij agents en engineers. FixControl houdt de twee als aparte schrijfpaden, zodat een notitie niet als antwoord naar buiten kan.",
    },
    q3: {
      q: "Wat gebeurt er als een Freshdesk-write mislukt?",
      a: "De schrijfactie blijft in de outbox staan en wordt opnieuw geprobeerd. Blijft Freshdesk weigeren, dan gaat de job naar de dead-letter en toont de tijdlijn een mislukte verzending — het ticket doet niet alsof het antwoord eruit ging.",
    },
    q4: {
      q: "Staan tweerichtings-syncs standaard aan?",
      a: "Nee. Syncs staan standaard uit en worden per verbinding ingeschakeld. Capability-health laat vervolgens zien welke ingeschakelde paden geconfigureerd zijn en werken.",
    },
    q5: {
      q: "Worden Freshdesk-antwoorden geaudit?",
      a: "Ja. Elk goedgekeurd antwoord en elke notitie heeft een regel in het beslissingsregister die vastlegt wie het goedkeurde en op welk bewijs, passend bij wat de tickethistorie toont.",
    },
  },
  related: {
    approvedReplies: {
      label: "Goedgekeurde antwoorden",
      description: "Door AI opgestelde, menselijk goedgekeurde klantantwoorden over Freshdesk en Jira.",
    },
    supportToEng: {
      label: "Support → engineering",
      description: "Maak van een support-ticket een gegoverneerde engineering-remediatie.",
    },
    jira: {
      label: "Jira",
      description: "Combineer Freshdesk support-context met gegoverneerde Jira-acties.",
    },
  },
  cta: {
    title: "Zie een antwoord wachten op goedkeuring",
    description:
      "Boek een demo: een ticket komt binnen via sync, AI stelt het antwoord op, en de verzending wacht tot iemand het goedkeurt.",
    primary: "Boek een demo",
    secondary: "Vraag het FixControl",
  },
};
